import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  Timestamp,
  DocumentReference,
  FieldValue,
} from 'firebase/firestore';

import {useWindowSize} from 'react-use-size';
import {useParams} from 'react-router-dom';
import {CourseId} from 'types/common';
import {ProjectId} from 'projects/types';
import {useState} from 'react';
import {useEffect} from 'react';
import {useCourseId} from 'hooks/router/useUrlParams';
import {useUserId} from 'hooks/user/useUserId';
import {getUserAssignmentMapDocumentRef} from '../firebase/models/userAssignmentMap';
import {readBuildCommit} from 'buildCommitMeta';

interface UsePromptPathResult {
  promptPath: string | undefined;
  isPromptPathLoading: boolean;
  promptPathError: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
export function usePromptPath(projectData: any): UsePromptPathResult {
  const [promptPath, setPromptPath] = useState<string | undefined>(undefined);
  const [isPromptPathLoading, setIsPromptPathLoading] = useState<boolean>(true);
  const [promptPathError, setPromptPathError] = useState<boolean>(false);

  const courseId = useCourseId();
  const userId = useUserId();

  useEffect(() => {
    if (isNullOrUndefined(userId)) {
      return;
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setIsPromptPathLoading(true);
    getPromptPath(projectData, courseId)
      .then(promptPath => {
        setPromptPath(promptPath);
        setPromptPathError(false);
      })
      .catch(error => {
        setPromptPathError(error);
      })
      .finally(() => {
        setIsPromptPathLoading(false);
      });
  }, [projectData, courseId, userId]);

  return {promptPath, isPromptPathLoading, promptPathError};
}

async function getPromptPath(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  projectData: any,
  courseId: CourseId,
): Promise<string | undefined> {
  if (!projectData) {
    return undefined;
  }
  // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  let assnId = projectData.assnId;
  if (!assnId) {
    return undefined;
  }

  return `/assns/${courseId}/assignments/${assnId}/docs/prompt`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
export function getSolnPath(projectData: any): string | undefined {
  if (!projectData) {
    return undefined;
  }
  // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  let assnId = projectData.assnId;
  if (!assnId) {
    return undefined;
  }
   
  // eslint-disable-next-line prefer-const, react-hooks/rules-of-hooks -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  let {courseId} = useParams();
  return `/assns/${courseId}/assignments/${assnId}/docs/soln`;
}

export const fetchAssignments = async courseId => {
  const db = getFirestore();
  const documentRef = doc(db, `assns/${courseId}`);
  const response = await getDoc(documentRef);

  return response.data();
};

export const getUserInfo = async userid => {
  const db = getFirestore();
  const docRef = doc(db, `users/${userid}`);
  const response = (await getDoc(docRef)).data();

  if (!response) return {};

  return response;
};

export const fetchAllAssignmentMetaData = async courseId => {
  const allAssnMetaData = {};
  const db = getFirestore();
  const collectionRef = collection(db, `assns/${courseId}/assignments`);
  const response = await getDocs(collectionRef);

  response.forEach(doc => {
    // doc.data() is never undefined for query doc snapshots
    allAssnMetaData[doc.id] = doc.data();
  });

  return allAssnMetaData;
};

export const fetchCreativeProjects = async userId => {
  const db = getFirestore();
  const docRef = doc(db, `users/${userId}/cip3/creativeProjects`);
  const response = (await getDoc(docRef)).data();

  // a few base cases
  if (!response) return [];
  if (!response.creativeProjects) return [];

  return response.creativeProjects;
};

export const fetchAssignmentInfo = async assnId => {
  const db = getFirestore();
  const docRef = doc(db, `assns/cip3/${assnId}/metaData`);
  const response = (await getDoc(docRef)).data();

  return response;
};

export const fetchProjectInfo = async projectId => {
  const db = getFirestore();
  const docRef = doc(db, `projects/${projectId}`);
  const response = (await getDoc(docRef)).data();

  return response;
};

export const loadUserAssnMap = async (
  userId: string,
  courseId: CourseId,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  onResponse: (data: any) => void,
) => {
  const docRef = getUserAssignmentMapDocumentRef({
    userId,
    courseId,
  });
  const response = await getDoc(docRef);
  if (response.exists()) {
    const data = response.data();
    onResponse(data);
  } else {
    onResponse({});
  }
};

export const editPublishedProjectNote = async (
  projectId: ProjectId,
  courseId: CourseId,
  note: string,
) => {
  const db = getFirestore();
  const docRef = doc(db, `published/${courseId}/studentPublished/${projectId}`);
  await updateDoc(docRef, {
    note,
  });
};

export const useIsMobile = (): boolean => {
  const {width} = useWindowSize();
  const isMobile = width < 768;
  return isMobile;
};

export const useIsTablet = (): boolean => {
  const {width} = useWindowSize();
  const isTablet = width < 1024;
  return isTablet;
};

/**
 * @name getNewPromise
 * @returns {object} - object with promise and resolve members
 */
export const getNewPromise = () => {
  let resolveFunc;
  // Returns object with promise member and resolve member
  return {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    promise: new Promise(function (resolve, reject) {
      resolveFunc = resolve;
    }),
    resolve: resolveFunc,
  };
};

/**
 * @deprecated Use `IS_DEVELOPMENT` instead.
 *
 * TODO: Replace all uses of this function with `IS_DEVELOPMENT`.
 *
 * Determines whether the current environment is development. This is useful for
 * features that should only be enabled/disabled in development mode. All tests
 * for build environment should use this function.
 */
export function isDevelopmentEnvironment(): boolean {
  return import.meta.env.DEV;
}

export {IS_DEVELOPMENT, IS_PRODUCTION} from '@cip/core/runtime';

/**
 * Short git commit SHA captured when the webapp bundle was built. Stamped onto
 * error log payloads so we can correlate errors back to a specific deploy.
 * Read from `index.html`, never baked into the bundle; see `buildCommitMeta.ts`.
 * Resolved by the same `@cip/tooling-config/build-info` helper the functions
 * build uses. Falls back to `'unknown'` when git was unavailable at build time.
 */
export const BUILD_COMMIT: string = readBuildCommit();

/**
 * Tests if two floating point numbers are approximately equal.
 *
 * Optional configurable error threshold. Should be used whenever trying to
 * compare floating point numbers for equality to avoid floating point
 * arithmetic errors.
 */
export function floatApproximatelyEquals(
  a: number,
  b: number,
  threshold: number = 1e-5,
): boolean {
  return Math.abs(a - b) < threshold;
}

/**
 * Tests that the given object is defined but empty (i.e. `{}` or `[]`).
 * @param data - The data to test.
 */
export function isEmptyObject(data: unknown): boolean {
  return typeof data === 'object' && !!data && Object.keys(data).length === 0;
}

/**
 * Tests if the given data is null or undefined.
 *
 * Also known as a "nullish" test. Often, you can use the nullish coalescing
 * operator `??` (a ?? b === !isNullOrUndefined(a) ? a : b) instead of this
 * function.
 *
 * Typed as a type predicate so callers get narrowing in the negative branch
 * (e.g., `if (isNullOrUndefined(x)) return;` makes `x` non-nullish after).
 */
export function isNullOrUndefined(data: unknown): data is null | undefined {
  return data === null || data === undefined;
}

/**
 * Validates an optional field. Checks if the field is either nullish or if
 * `typeof field === expectedType`.
 */
export const validateOptionalPrimitiveField = (
  field: unknown,
  expectedType: string,
): boolean => {
  if (isNullOrUndefined(field)) {
    return true;
  }
  return typeof field === expectedType;
};

/**
 * Validates an array field.
 */
export const validateArrayField = ({
  field,
  validateElement,
  isOptional = false,
}: {
  /** The array to validate. */
  field: unknown;
  /** A function that validates an element of the array. */
  validateElement: (element: unknown) => boolean;
  /** Whether the field is optional. If true, the field is allowed to be
   * nullish. Defaults to false. */
  isOptional?: boolean;
}): boolean => {
  if (isNullOrUndefined(field)) {
    return isOptional;
  }
  if (!Array.isArray(field)) {
    return false;
  }
  return field.every(validateElement);
};

/**
 * Validates an object field.
 */
export const validateObjectField = ({
  field,
  validateElement,
  isOptional = false,
}: {
  /** The object to validate. */
  field: unknown;
  /** A function that validates an element of the object. */
  validateElement: (key: string, element: unknown) => boolean;
  /** Whether the field is optional. If true, the field is allowed to be
   * nullish. Defaults to false. */
  isOptional?: boolean;
}): boolean => {
  if (isNullOrUndefined(field)) {
    return isOptional;
  }
  if (typeof field !== 'object') {
    return false;
  }
  return Object.entries(field).every(([key, value]) =>
    validateElement(key, value),
  );
};

const MAX_DEPTH = 20;
/**
 * Converts an object to a Firebase safe object. This converts Node ES6 maps to
 * simple objects and casts nullish values to `null`. Skips sanitizing native
 * Firebase objects like `Timestamp`, `DocumentReference`, and `FieldValue`.
 * Recursively applies to all nested objects and arrays.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
export function toFirebaseSafeObject(obj: unknown, currDepth: number = 0): any {
  if (currDepth > MAX_DEPTH) {
    return null;
  }
  try {
    if (typeof obj === 'object') {
      if (!obj) {
        return null;
      }
      if (Array.isArray(obj)) {
        return obj.map(value => toFirebaseSafeObject(value, currDepth + 1));
      } else if (obj instanceof Map) {
        return Object.fromEntries(
          Array.from(obj.entries()).map(([key, value]) => [
            key,
            toFirebaseSafeObject(value, currDepth + 1),
          ]),
        );
      } else if (obj instanceof Set) {
        return Array.from(obj).map(value =>
          toFirebaseSafeObject(value, currDepth + 1),
        );
      } else if (obj instanceof Timestamp) {
        // Don't try to sanitize the Timestamp since it's a native Firebase
        // object.
        return obj;
      } else if (obj instanceof DocumentReference) {
        // Don't try to sanitize the DocumentReference since it's a native
        // Firebase object.
        return obj;
      } else if (obj instanceof FieldValue) {
        // Don't try to sanitize the FieldValue since it's a native Firebase
        // object.
        return obj;
      } else {
        return Object.fromEntries(
          Object.entries(obj).map(([key, value]) => [
            key,
            toFirebaseSafeObject(value, currDepth + 1),
          ]),
        );
      }
    } else if (typeof obj === 'undefined') {
      return null;
    } else if (typeof obj === 'string') {
      return obj;
    } else if (typeof obj === 'number') {
      return obj;
    } else if (typeof obj === 'boolean') {
      return obj;
    } else {
      throw new Error(
        'Tried converting non-primitive type to Firebase safe object.',
      );
    }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  } catch (e: any) {
    throw new Error(
      'Failed to convert object to Firebase safe object. Error: ' + e,
    );
  }
}

/**
 * Determines if the current browser is an in-app browser.
 *
 * In-app browsers are typically used by social media platforms to provide a
 * more seamless experience for users, but they don't support logging in
 * via "Sign in with Google".
 */
export function isInAppBrowser(): boolean {
  return /(Instagram|LinkedIn|Facebook|Twitter|Pinterest|Reddit|Telegram|WhatsApp|Discord)/i.test(
    navigator.userAgent,
  );
}

/**
 * Picks a random element from an array.
 */
export function pickRandomElement<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

export function isIterable(object: unknown): object is Iterable<unknown> {
  return (
    typeof object === 'object' &&
    object !== null &&
    typeof object[Symbol.iterator] === 'function'
  );
}
